// DocTemplatePreviewPane — the right-hand pane of the document-templates explorer.
// Renders the selected template in the browser:
//   - PDF  → the bytes streamed into a sandboxed <iframe> (native viewer);
//   - XLSX → the BFF's styled HTML render (exceljs fills/fonts/borders/merges/
//     column widths as inline styles) in a sandboxed <iframe> srcdoc;
//   - DOCX → the BFF's mammoth HTML render (semantic, not pixel-perfect);
//   - anything else → a download fallback.
//
// The iframe is `sandbox`ed with no `allow-scripts`, so even though the HTML
// originates from user-uploaded documents it can neither run script nor reach
// the parent origin.

import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Download, FileText, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  downloadDocTemplate,
  docTemplateObjectUrl,
  useGetDocTemplateFieldsQuery,
  useGetDocTemplatePreviewQuery,
  type DocTemplate
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { parentPathOf } from '../../components/explorer'
import { downloadName, formatBytes, previewKind } from './doc-helpers'

export interface DocTemplatePreviewPaneProps {
  item: DocTemplate
  /** Request deletion (parent owns the confirm dialog). */
  onDelete: (item: DocTemplate) => void
}

/** Base stylesheet injected into the HTML-preview iframe (paper-like). */
const PREVIEW_CSS = `
  :root { color-scheme: light; }
  body {
    margin: 0;
    padding: 32px 40px;
    background: #fff;
    color: #1a1a1a;
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.55;
  }
  h1, h2, h3 { line-height: 1.25; }
  table { border-collapse: collapse; margin: 12px 0; max-width: 100%; }
  td, th { border: 1px solid #d4d4d8; padding: 4px 8px; vertical-align: top; }
  section + section { margin-top: 28px; }
  section > h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .05em; color: #71717a; }
  img { max-width: 100%; height: auto; }
  p.truncated, p.empty { color: #a1a1aa; font-style: italic; font-size: 12px; }
`

/** Wrap a BFF HTML body fragment into a complete, styled document. */
function wrapHtml(fragment: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>${PREVIEW_CSS}</style></head><body>${fragment}</body></html>`
}

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: { message?: unknown } }).data
    if (data && typeof data.message === 'string') return data.message
  }
  return fallback
}

const PaneShell = ({
  item,
  onDelete,
  children
}: DocTemplatePreviewPaneProps & { children: React.ReactNode }) => {
  const [downloading, setDownloading] = useState(false)
  const folder = parentPathOf(item.path)

  const handleDownload = async (): Promise<void> => {
    setDownloading(true)
    try {
      const fileName = downloadName(item.name, item.format)
      await downloadDocTemplate(item.id, fileName)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-line px-4 py-2.5">
        <FileText className="size-4 shrink-0 text-ink-3" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink">{item.name}</div>
          <div className="flex flex-wrap items-center gap-2 text-11 text-ink-3">
            {item.format ? (
              <Badge variant="outline" className="uppercase">
                {item.format}
              </Badge>
            ) : null}
            {folder ? <span>/{folder}</span> : null}
            <span>{formatBytes(item.size_bytes)}</span>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          disabled={downloading}
          onClick={() => void handleDownload()}
        >
          {downloading ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <Download aria-hidden />
          )}
          Download
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={`Delete ${item.name}`}
          onClick={() => onDelete(item)}
        >
          <Trash2 aria-hidden />
        </Button>
      </header>
      <div className="min-h-0 flex-1 overflow-auto bg-bg-sunk/40">{children}</div>
    </div>
  )
}

const CenteredState = ({
  icon,
  children
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
    {icon}
    <p className="m-0 max-w-md text-sm text-ink-3">{children}</p>
  </div>
)

/** PDF: fetch authed bytes → object URL → native iframe viewer. */
const PdfBody = ({ item }: { item: DocTemplate }) => {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let revoked = false
    let objectUrl: string | null = null
    setUrl(null)
    setError(null)
    docTemplateObjectUrl(item.id)
      .then((next) => {
        if (revoked) {
          URL.revokeObjectURL(next)
          return
        }
        objectUrl = next
        setUrl(next)
      })
      .catch((err: unknown) => {
        if (!revoked) setError(err instanceof Error ? err.message : 'Failed to load PDF')
      })
    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [item.id])

  if (error) {
    return (
      <CenteredState icon={<AlertCircle className="size-8 text-st-down" aria-hidden />}>
        {error}
      </CenteredState>
    )
  }
  if (!url) {
    return (
      <CenteredState icon={<Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />}>
        Loading preview…
      </CenteredState>
    )
  }
  return <iframe src={url} title={`${item.name} preview`} className="size-full border-0" />
}

/** DOCX/XLSX: BFF HTML render → sandboxed srcdoc iframe. */
const HtmlBody = ({ item }: { item: DocTemplate }) => {
  const templatePreview = useGetDocTemplatePreviewQuery(item.id)
  const srcDoc = useMemo(
    () => (templatePreview.data ? wrapHtml(templatePreview.data.html) : null),
    [templatePreview.data]
  )

  if (templatePreview.isLoading) {
    return (
      <CenteredState icon={<Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />}>
        Rendering preview…
      </CenteredState>
    )
  }
  if (templatePreview.isError || srcDoc === null) {
    return (
      <CenteredState icon={<AlertCircle className="size-8 text-st-down" aria-hidden />}>
        {errorMessage(
          templatePreview.error,
          'Could not render this document. Try downloading it instead.'
        )}
      </CenteredState>
    )
  }
  return (
    <iframe
      // sandbox="" → no scripts, no same-origin: user-doc HTML is inert.
      sandbox=""
      srcDoc={srcDoc}
      title={`${item.name} preview`}
      className="size-full border-0 bg-white"
    />
  )
}

/** A strip of the merge fields the documents agent can fill. */
const TemplateFieldsStrip = ({ id }: { id: string }) => {
  const { data, isLoading } = useGetDocTemplateFieldsQuery(id)
  const fields = data?.fields ?? []
  if (isLoading || fields.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-line bg-bg-elev px-4 py-2">
      <span className="text-11 uppercase tracking-wider text-ink-3">
        {fields.length} merge field{fields.length === 1 ? '' : 's'}
      </span>
      {fields.map((field) => (
        <Badge key={field} variant="secondary" className="font-mono text-11">
          {field}
        </Badge>
      ))}
    </div>
  )
}

const DocTemplatePreviewPane = (props: DocTemplatePreviewPaneProps) => {
  const { item } = props
  const previewType = previewKind(item.format)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1">
        <PaneShell {...props}>
          {previewType === 'pdf' ? (
            <PdfBody item={item} />
          ) : previewType === 'html' ? (
            <HtmlBody item={item} />
          ) : (
            <CenteredState icon={<FileText className="size-8 text-ink-3" aria-hidden />}>
              No in-browser preview for “.{item.format}” files. Download it to view.
            </CenteredState>
          )}
        </PaneShell>
      </div>
      <TemplateFieldsStrip id={item.id} />
    </div>
  )
}

export default DocTemplatePreviewPane
